import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { tweetRec3 } from '../../../core/services/twitter';

/**
 * ツイート文字数カウント
 * 全角2バイト、半角1バイト、改行1バイト + #myrunwallet(13バイト)
 */
function countBytes(text) {
  return (
    text.split('').reduce((count, char) => {
      if (char === '\n') return count + 1;
      return count + (char.match(/[^\x00-\x7F]/) ? 2 : 1);
    }, 0) + 13
  );
}

export function TweetDialog({ certificate, open, onClose }) {
  const [tweet, setTweet] = useState('');
  const [postWithImage, setPostWithImage] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (certificate && open) {
      const dateStr = certificate.eventDate?.toDate?.() ? certificate.eventDate.toDate().toLocaleDateString() : '';
      const eventNameNoSpace = certificate.eventName?.replace(/\s+/g, '') || '';
      setTweet(`#${eventNameNoSpace} (${dateStr}開催)\nグロス: ${certificate.time}\nネット: ${certificate.netTime}`);
      setPostWithImage(false);
      setError(null);
      setResult(null);
    }
  }, [certificate, open]);

  const byteCount = countBytes(tweet);
  const isOverLimit = byteCount > 280;

  const handlePost = async () => {
    if (!certificate) return;
    setPosting(true);
    setError(null);
    try {
      const data = await tweetRec3(certificate.id, tweet, postWithImage);
      setResult(data);
    } catch (err) {
      console.error('Failed to tweet:', err);
      setError('ポストに失敗しました');
    } finally {
      setPosting(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Xにポスト</DialogTitle>
      <DialogContent>
        {result ? (
          <Alert severity="success" sx={{ mt: 1 }}>
            ポストしました！{' '}
            <Link href={`https://x.com/${result.screenName}/status/${result.tweetId}`} target="_blank" rel="noopener">
              ポストを見る
            </Link>
          </Alert>
        ) : (
          <>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={tweet}
              onChange={(e) => setTweet(e.target.value)}
              disabled={posting}
              sx={{ mt: 1 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={postWithImage}
                    onChange={(e) => setPostWithImage(e.target.checked)}
                    disabled={posting}
                    size="small"
                  />
                }
                label={<Typography variant="body2">画像付きでポスト</Typography>}
              />
              <Typography
                variant="body2"
                sx={{
                  color: isOverLimit ? 'error.main' : 'text.secondary',
                  fontWeight: isOverLimit ? 'bold' : 'normal',
                }}
              >
                {byteCount / 2}
              </Typography>
            </Box>
            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button onClick={handleClose}>閉じる</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={posting}>
              キャンセル
            </Button>
            <Button onClick={handlePost} variant="contained" disabled={posting || isOverLimit}>
              {posting ? <CircularProgress size={20} /> : 'ポスト'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
